import { ID_NONE } from 'app/common/lib/util/satisfies-filter';
import { LabelColor, LabelName } from 'app/src/components/BoardTableView/types';
import { FilterableCard } from 'app/src/components/ViewsFilter/types';
import {
  BoardTableViewFilter,
  CardFilterCriteria,
} from './BoardTableViewFilter';

export class LabelsFilter
  extends Map<LabelColor, Set<LabelName>>
  implements BoardTableViewFilter {
  filterLength() {
    const labels = this.getLabelsForServer();
    return labels ? labels.length : 0;
  }

  isEmpty(): boolean {
    return this.filterLength() === 0;
  }

  isEnabled(color: LabelColor, name: LabelName) {
    return this.get(color)?.has(name) || false;
  }

  disable(color: LabelColor, name: LabelName) {
    const colorMap = this.get(color);

    if (colorMap?.has(name)) {
      colorMap.delete(name);
      if (colorMap.size === 0) {
        this.delete(color);
      }
    }
  }

  enable(color: LabelColor, name: LabelName) {
    const colorMap = this.get(color);
    if (colorMap) {
      colorMap.add(name);
    } else {
      this.set(color, new Set([name]));
    }
  }

  toggle(color: LabelColor, name: LabelName) {
    if (this.isEnabled(color, name)) {
      this.disable(color, name);
    } else {
      this.enable(color, name);
    }

    // Returns a new instance so that we can use it for `setState`.
    const newLabelsFilter = new LabelsFilter(this);
    return newLabelsFilter;
  }

  equals(otherFilter: LabelsFilter): boolean {
    const setEquals = (
      set1: Set<string | null | undefined> | undefined,
      set2: Set<string | null | undefined> | undefined,
    ): boolean => {
      if (set1 === undefined && set2 === undefined) return true;
      else if (set1 === undefined || set2 === undefined) return false;
      return (
        set1.size === set2.size && [...set1].every((value) => set2.has(value))
      );
    };

    const colors = new Set(this.keys());

    return (
      setEquals(colors, new Set(otherFilter.keys())) &&
      Array.from(colors).every((color) =>
        setEquals(this.get(color), otherFilter.get(color)),
      )
    );
  }

  satisfiesLabelsFilter(
    cardLabels: FilterableCard['labels'],
    isAnd?: boolean,
  ): boolean {
    if (this.isEmpty()) {
      return true;
    }

    const showCardsWithNoLabels = this.isEnabled('no labels', 'no labels');

    if (isAnd) {
      if (showCardsWithNoLabels && this.filterLength() >= 2) {
        return false;
      }

      if (showCardsWithNoLabels && cardLabels.length === 0) {
        return true;
      }

      const filterLabelStrings = this.getLabelsForServer() || [];
      const cardLabelStrings = cardLabels.map(({ color, name }) =>
        name ? `${color}:${name}` : `${color}`,
      );

      return filterLabelStrings.every((filterLabelString) => {
        return cardLabelStrings.includes(filterLabelString);
      });
    } else {
      if (showCardsWithNoLabels && cardLabels.length === 0) {
        return true;
      }

      return cardLabels.some(({ color, name }) => {
        return this.isEnabled(color, name);
      });
    }
  }

  getLabelsForServer(): string[] | null {
    const labels: string[] = [];

    for (const [color, colorMap] of this.entries()) {
      if (color !== 'no labels') {
        for (const name of colorMap.values()) {
          labels.push(
            name.length
              ? `${color}:${
                  // These characters have special meaning when parsing labels
                  // so encode the whole name if we see any of them. The server
                  // always decodes the name portion, but we avoid an extra
                  // level of encoding if we can to keep the URL a little more
                  // readable
                  /[:,%]/.test(name) ? encodeURIComponent(name) : name
                }`
              : `${color}`,
          );
        }
      } else {
        labels.push(ID_NONE);
      }
    }

    if (labels.length === 0) {
      return null;
    }

    return labels;
  }

  toUrlParams(): {
    [key: string]: string | null;
  } {
    const labels = this.getLabelsForServer();
    const labelString = labels ? labels.join(',') : null;
    return {
      labels: labelString,
    };
  }

  fromUrlParams({ labels: labelsString }: { [key: string]: string | null }) {
    const labels = labelsString?.split(',') || [];

    for (const label of labels) {
      this._deserializeSingleLabel(label);
    }
  }

  _deserializeSingleLabel(labelString: string) {
    if (labelString === ID_NONE) {
      this.enable('no labels', 'no labels');
    }

    const splitLabel = labelString.split(':');
    const labelColor = splitLabel[0] === 'null' ? null : splitLabel[0];
    const labelName =
      splitLabel.length > 1 ? decodeURIComponent(splitLabel[1]) : '';
    this.enable(labelColor, labelName);
  }

  serializeToView(): CardFilterCriteria {
    return { labels: this.getLabelsForServer() || [] };
  }

  deserializeFromView(cardFilterCriteria: CardFilterCriteria) {
    const labels = cardFilterCriteria.labels;
    labels?.forEach((label) => this._deserializeSingleLabel(label));
  }
}
